/**
 * GroupManager.jsx - Gestión de Grupos dentro de Secciones
 * 
 * Permite a teachers/admins:
 * - Seleccionar una sección de curso
 * - Crear grupos dentro de la sección (4-6 miembros)
 * - Asignar estudiantes a grupos
 * - Transferir estudiantes entre grupos
 * - Ver historial de cambios de membresía
 * - Archivar grupos
 * 
 * Solo accesible para usuarios con rol 'teacher' o 'admin'
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Save,
  X,
  Loader,
  AlertCircle,
  CheckCircle,
  UserPlus,
  UserMinus,
  ArrowRightLeft,
  History,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Calendar,
  FileText,
  ArrowLeft
} from 'lucide-react';
import apiClient from '../services/apiClient';

const GroupManager = () => {
  const { user } = useAuth();
  const { sectionId } = useParams();
  const navigate = useNavigate();
  
  // Estados principales
  const [section, setSection] = useState(null);
  const [groups, setGroups] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Estados del formulario
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [formData, setFormData] = useState({
    groupName: '',
    groupNumber: 1,
    members: []
  });
  
  // Estados de gestión de miembros
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedGroupForMember, setSelectedGroupForMember] = useState(null);
  const [selectedStudentToAdd, setSelectedStudentToAdd] = useState('');
  
  // Estados de transferencia
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({
    userId: '',
    sourceGroupId: '',
    targetGroupId: ''
  });
  
  // Estados de historial
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Estados de UI
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Control de acceso
  if (!user || (user.role !== 'teacher' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Restringido</h2>
          <p className="text-gray-600 mb-6">
            Esta sección es solo para instructores y administradores.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }
  
  // Cargar datos al montar
  useEffect(() => {
    if (sectionId) {
      fetchSectionData();
      fetchGroups();
      fetchAvailableStudents();
    }
  }, [sectionId]);
  
  // Auto-limpiar mensajes después de 5 segundos
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  /**
   * Obtener datos de la sección
   */
  const fetchSectionData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/sections/${sectionId}`);
      
      if (response.data.success) {
        setSection(response.data.section);
      }
    } catch (err) {
      console.error('Error al cargar sección:', err);
      setError(err.response?.data?.error || 'Error al cargar la sección');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Obtener todos los grupos de la sección
   */
  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/groups/section/${sectionId}`);
      
      if (response.data.success) {
        setGroups(response.data.groups || []);
      }
    } catch (err) {
      console.error('Error al cargar grupos:', err);
      setError(err.response?.data?.error || 'Error al cargar los grupos');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Obtener estudiantes disponibles (role=student, no asignados a grupos de esta sección)
   */
  const fetchAvailableStudents = async () => {
    try {
      const response = await apiClient.get(`/groups/section/${sectionId}/available-students`);
      setAvailableStudents(response.data.students || []);
    } catch (err) {
      console.error('Error al cargar estudiantes disponibles:', err);
      setAvailableStudents([]);
    }
  };

  /**
   * Crear nuevo grupo
   */
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      // Validaciones
      if (!formData.groupName.trim()) {
        setError('El nombre del grupo es requerido');
        return;
      }
      
      if (formData.members.length > 0 && (formData.members.length < 4 || formData.members.length > 6)) {
        setError('Los grupos deben tener entre 4 y 6 miembros');
        return;
      }
      
      const response = await apiClient.post('/groups', {
        sectionId: sectionId,
        groupNumber: formData.groupNumber,
        groupName: formData.groupName,
        members: formData.members
      });
      
      if (response.data.success) {
        setSuccess('Grupo creado exitosamente');
        resetForm();
        fetchGroups();
      }
    } catch (err) {
      console.error('Error al crear grupo:', err);
      setError(err.response?.data?.error || 'Error al crear el grupo');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Agregar miembro a un grupo
   */
  const handleAddMember = async () => {
    if (!selectedStudentToAdd || !selectedGroupForMember) {
      setError('Selecciona un estudiante');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.post(
        `/groups/${selectedGroupForMember}/members`,
        { userId: selectedStudentToAdd }
      );
      
      if (response.data.success) {
        setSuccess('Estudiante agregado al grupo');
        setShowAddMemberModal(false);
        setSelectedStudentToAdd('');
        setSelectedGroupForMember(null);
        fetchGroups();
        fetchAvailableStudents();
      }
    } catch (err) {
      console.error('Error al agregar miembro:', err);
      setError(err.response?.data?.error || 'Error al agregar el estudiante');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Remover miembro de un grupo
   */
  const handleRemoveMember = async (groupId, userId, userName) => {
    if (!confirm(`¿Remover a ${userName} del grupo?`)) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.delete(`/groups/${groupId}/members/${userId}`);
      
      if (response.data.success) {
        setSuccess('Estudiante removido del grupo');
        fetchGroups();
        fetchAvailableStudents();
      }
    } catch (err) {
      console.error('Error al remover miembro:', err);
      setError(err.response?.data?.error || 'Error al remover el estudiante');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Transferir miembro entre grupos
   */
  const handleTransferMember = async () => {
    if (!transferData.userId || !transferData.targetGroupId) {
      setError('Completa todos los campos');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.post(
        `/groups/${transferData.sourceGroupId}/transfer`,
        {
          userId: transferData.userId,
          targetGroupId: transferData.targetGroupId
        }
      );
      
      if (response.data.success) {
        setSuccess('Estudiante transferido exitosamente');
        setShowTransferModal(false);
        setTransferData({ userId: '', sourceGroupId: '', targetGroupId: '' });
        fetchGroups();
      }
    } catch (err) {
      console.error('Error al transferir miembro:', err);
      setError(err.response?.data?.error || 'Error al transferir el estudiante');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ver historial de un grupo
   */
  const handleViewHistory = async (groupId) => {
    try {
      setLoadingHistory(true);
      setShowHistoryModal(true);
      
      const response = await apiClient.get(`/groups/${groupId}/history`);
      
      if (response.data.success) {
        setHistoryData(response.data.history || []);
      }
    } catch (err) {
      console.error('Error al cargar historial:', err);
      setError(err.response?.data?.error || 'Error al cargar el historial');
      setShowHistoryModal(false);
    } finally {
      setLoadingHistory(false);
    }
  };

  /**
   * Archivar grupo
   */
  const handleArchiveGroup = async (groupId, groupName) => {
    if (!confirm(`¿Archivar el grupo "${groupName}"?`)) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.put(`/groups/${groupId}`, {
        status: 'archived'
      });
      
      if (response.data.success) {
        setSuccess('Grupo archivado exitosamente');
        fetchGroups();
      }
    } catch (err) {
      console.error('Error al archivar grupo:', err);
      setError(err.response?.data?.error || 'Error al archivar el grupo');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset del formulario
   */
  const resetForm = () => {
    setFormData({
      groupName: '',
      groupNumber: groups.length + 1,
      members: []
    });
    setShowCreateForm(false);
    setEditingGroupId(null);
  };

  /**
   * Toggle expansión de grupo
   */
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  /**
   * Iniciar transferencia
   */
  const startTransfer = (sourceGroupId, userId, userName) => {
    setTransferData({
      userId,
      sourceGroupId,
      targetGroupId: '',
      userName
    });
    setShowTransferModal(true);
  };

  // Loading state
  if (loading && groups.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header con info de sección */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1">
              <button
                onClick={() => navigate('/teaching/sections')}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-3"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver a Secciones
              </button>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Grupos de la Sección
              </h1>
              {section && (
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    <span>{section.courseId}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{section.schedule}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{groups.length} grupo(s)</span>
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={() => {
                setFormData({
                  groupName: '',
                  groupNumber: groups.length + 1,
                  members: []
                });
                setShowCreateForm(true);
              }}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Nuevo Grupo
            </button>
          </div>
        </div>

        {/* Mensajes de éxito/error */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800">{success}</span>
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Formulario de creación */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Crear Nuevo Grupo</h2>
            
            <form onSubmit={handleCreateGroup} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del Grupo *
                  </label>
                  <input
                    type="text"
                    value={formData.groupName}
                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                    placeholder="Ej: Los Innovadores"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Grupo *
                  </label>
                  <input
                    type="number"
                    value={formData.groupNumber}
                    onChange={(e) => setFormData({ ...formData, groupNumber: parseInt(e.target.value) })}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                <p>✓ Puedes crear el grupo vacío y agregar miembros después</p>
                <p>✓ Los grupos deben tener entre 4 y 6 miembros para estar activos</p>
              </div>
              
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Crear Grupo
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista de grupos */}
        <div className="space-y-4">
          {groups.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No hay grupos todavía
              </h3>
              <p className="text-gray-600 mb-4">
                Crea el primer grupo para esta sección
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Crear Grupo
              </button>
            </div>
          ) : (
            groups.map(group => (
              <div key={group.groupId} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Header del grupo */}
                <div className="p-4 md:p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-900">
                          {group.groupName}
                        </h3>
                        <span className="text-sm px-2 py-1 bg-purple-100 text-purple-700 rounded">
                          Grupo #{group.groupNumber}
                        </span>
                        {group.status === 'archived' && (
                          <span className="text-sm px-2 py-1 bg-gray-100 text-gray-700 rounded">
                            Archivado
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{group.members?.length || 0} miembro(s)</span>
                        </div>
                        {group.members && group.members.length > 0 && group.members.length < 4 && (
                          <span className="text-orange-600 font-medium">
                            ⚠️ Requiere más miembros (mínimo 4)
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => toggleGroup(group.groupId)}
                      className="text-gray-500 hover:text-gray-700 p-2"
                    >
                      {expandedGroups[group.groupId] ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Contenido expandible */}
                {expandedGroups[group.groupId] && (
                  <div className="p-4 md:p-6 bg-gray-50">
                    
                    {/* Lista de miembros */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">Miembros del Grupo</h4>
                        {group.status !== 'archived' && (
                          <button
                            onClick={() => {
                              setSelectedGroupForMember(group.groupId);
                              setShowAddMemberModal(true);
                            }}
                            className="text-purple-600 hover:text-purple-700 text-sm flex items-center gap-1"
                          >
                            <UserPlus className="w-4 h-4" />
                            Agregar Miembro
                          </button>
                        )}
                      </div>
                      
                      {group.members && group.members.length > 0 ? (
                        <div className="space-y-2">
                          {group.members.map((member, index) => (
                            <div key={member._id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                  <span className="text-purple-700 font-semibold text-sm">
                                    {index + 1}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{member.nombre}</p>
                                  <p className="text-sm text-gray-600">{member.email}</p>
                                </div>
                              </div>
                              
                              {group.status !== 'archived' && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => startTransfer(group.groupId, member._id, member.nombre)}
                                    className="text-blue-600 hover:text-blue-700 p-1"
                                    title="Transferir a otro grupo"
                                  >
                                    <ArrowRightLeft className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveMember(group.groupId, member._id, member.nombre)}
                                    className="text-red-600 hover:text-red-700 p-1"
                                    title="Remover del grupo"
                                  >
                                    <UserMinus className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-gray-300">
                          <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600">No hay miembros en este grupo</p>
                          {group.status !== 'archived' && (
                            <button
                              onClick={() => {
                                setSelectedGroupForMember(group.groupId);
                                setShowAddMemberModal(true);
                              }}
                              className="mt-3 text-purple-600 hover:text-purple-700 text-sm"
                            >
                              Agregar primer miembro
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Acciones del grupo */}
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => navigate(`/teaching/sections/${sectionId}/groups/${group.groupId}/weekly/1`)}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        Entregables Semanales
                      </button>
                      
                      <button
                        onClick={() => handleViewHistory(group.groupId)}
                        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                      >
                        <History className="w-4 h-4" />
                        Ver Historial
                      </button>
                      
                      {group.status !== 'archived' && (
                        <button
                          onClick={() => handleArchiveGroup(group.groupId, group.groupName)}
                          className="flex items-center gap-2 px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                          Archivar Grupo
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Modal: Agregar Miembro */}
        {showAddMemberModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Agregar Estudiante</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecciona un estudiante
                </label>
                
                {availableStudents.length > 0 ? (
                  <select
                    value={selectedStudentToAdd}
                    onChange={(e) => setSelectedStudentToAdd(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">-- Selecciona --</option>
                    {availableStudents.map(student => (
                      <option key={student._id} value={student._id}>
                        {student.nombre} ({student.email})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-center py-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-600 text-sm mb-2">
                      No hay estudiantes disponibles
                    </p>
                    <p className="text-gray-500 text-xs">
                      Todos los estudiantes están asignados a grupos
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setShowAddMemberModal(false);
                    setSelectedStudentToAdd('');
                    setSelectedGroupForMember(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddMember}
                  disabled={!selectedStudentToAdd}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  Agregar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Transferir Miembro */}
        {showTransferModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Transferir Estudiante
              </h3>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Estudiante: <span className="font-semibold">{transferData.userName}</span>
                </p>
                
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grupo destino
                </label>
                <select
                  value={transferData.targetGroupId}
                  onChange={(e) => setTransferData({ ...transferData, targetGroupId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">-- Selecciona un grupo --</option>
                  {groups
                    .filter(g => g.groupId !== transferData.sourceGroupId && g.status !== 'archived')
                    .map(g => (
                      <option key={g.groupId} value={g.groupId}>
                        {g.groupName} (Grupo #{g.groupNumber}) - {g.members?.length || 0} miembros
                      </option>
                    ))
                  }
                </select>
              </div>
              
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setTransferData({ userId: '', sourceGroupId: '', targetGroupId: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleTransferMember}
                  disabled={!transferData.targetGroupId}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Transferir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Historial */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-96 overflow-y-auto">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Historial de Cambios
              </h3>
              
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Loader className="w-8 h-8 text-purple-600 animate-spin" />
                </div>
              ) : historyData.length > 0 ? (
                <div className="space-y-3">
                  {historyData.map((entry, index) => (
                    <div key={index} className="border-l-4 border-purple-500 pl-4 py-2">
                      <p className="font-medium text-gray-900">
                        {entry.action === 'added' && '➕ Miembro agregado'}
                        {entry.action === 'removed' && '➖ Miembro removido'}
                        {entry.action === 'transferred_in' && '⬅️ Transferido desde otro grupo'}
                        {entry.action === 'transferred_out' && '➡️ Transferido a otro grupo'}
                      </p>
                      <p className="text-sm text-gray-600">
                        Estudiante: {entry.userId?.nombre || 'Desconocido'}
                      </p>
                      <p className="text-sm text-gray-600">
                        Por: {entry.performedBy?.nombre || 'Sistema'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(entry.timestamp).toLocaleString('es-MX')}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-600 py-8">
                  No hay cambios registrados
                </p>
              )}
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowHistoryModal(false);
                    setHistoryData([]);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupManager;
